<template>
  <div>
    <label
      v-if="label"
      :for="id"
    >{{ label }}<sup v-if="required">*</sup></label>
    <div class="rs-form-tags-input-wrapper">
      <input
        :id="inputId"
        ref="tagsInput"
        class="rs-form-tags-input"
        :placeholder="placeholder"
        :disabled="disabled"
      >
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import Tagify from '@yaireo/tagify';

type IVoerroTagsInputVal = { key: string, value: string }[];

export default defineComponent({

  name: 'RsFormTagsInput',

  props: {
    id: {
      type: String,
      default: 'rs-form-tags-input',
    },

    label: {
      type: String,
    },

    required: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * NOTE: no consumer in this codebase passes a whitelist for suggestions, so this
     * has always been functionally inert (Tagify's dropdown suggestions require a
     * `whitelist` to have anything to show). Kept for prop-API backward compatibility.
     */
    typeahead: {
      type: Boolean,
      default: false,
    },

    limit: {
      type: Number,
      default: 0,
    },

    addTagsOnSpace: {
      type: Boolean,
      default: false,
    },

    addTagsOnComma: {
      type: Boolean,
      default: false,
    },

    addTagsOnBlur: {
      type: Boolean,
      default: false,
    },

    placeholder: {
      type: String,
    },

    readonly: {
      type: String,
    },

    validate: {
      type: Function as PropType<(val: IVoerroTagsInputVal[0] | string) => boolean>,
    },

    value: {
      type: String,
      default: '',
    },

    /**
     * Vue 3 v-model binding. Kept alongside the legacy `value`/`input` pair so
     * both `v-model` (modelValue/update:modelValue) and explicit `:value`+`@input`
     * consumers work. `undefined` means the parent did not bind v-model, so we
     * fall back to `value` (see the `boundValue` computed).
     */
    modelValue: {
      type: String,
      default: undefined,
    },
  },

  emits: ['input', 'is-valid-input-on-blur', 'on-focus', 'on-search', 'on-validate', 'tag-added', 'tag-removed', 'update:modelValue'],

  data() {
    return {
      inputId: `vue-tagsinput-input-${this.id}` as string,
      tagifyInstance: null as Tagify | null,
      lastEmittedValue: null as string | null,
    };
  },

  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): string {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
  },

  watch: {
    boundValue(newVal: string) {
      if (!this.tagifyInstance) return;
      const currentValue = (this.tagifyInstance.value ?? []).map((t: any) => t.value).join(',');
      if (currentValue === (newVal || '')) return;
      this.tagifyInstance.removeAllTags();
      if (newVal) {
        this.tagifyInstance.addTags(newVal.split(','));
      }
    },

    disabled(val: boolean) {
      // setDisabled(), not setReadonly(): tagify seeds `settings.disabled` from the source
      // input's attribute, and its event binding bails while that flag is set. setReadonly()
      // only ever touches `settings.readonly`, so re-enabling left the control inert.
      this.tagifyInstance?.setDisabled(val);
    },
  },

  mounted() {
    const instance = new Tagify(this.$refs.tagsInput as HTMLInputElement, {
      delimiters: this.buildDelimiters(),
      addTagOnBlur: this.addTagsOnBlur,
      maxTags: this.limit || Infinity,
      duplicates: false,
      // Keep the native input's value a plain comma-joined list (tagify's
      // default serializes to JSON, which leaks into validators/serializers
      // that read the DOM value).
      originalInputValueFormat: (values: { value: string }[]) => values.map((t) => t.value).join(','),
      // Required so origSettings.dropdown is defined; setReadonly() restores
      // dropdown.enabled from origSettings and crashes if it was never set.
      dropdown: { enabled: false },
      validate: (tagData: { value: string }) => {
        const result = this.validate ? this.validate(tagData.value) : true;
        this.$emit('on-validate', result);
        return result;
      },
    } as any);
    this.tagifyInstance = instance;

    if (this.boundValue) {
      instance.addTags(this.boundValue.split(','));
    }
    if (this.disabled) {
      instance.setDisabled(true);
    }

    instance.on('change', () => this.emitInput());
    instance.on('add', (e: any) => this.onTagAdded({ key: '', value: e.detail.data.value }));
    instance.on('remove', (e: any) => this.onTagRemoved({ key: '', value: e.detail.data.value }));
    instance.on('focus', () => this.$emit('on-focus'));
    instance.on('blur', () => this.isValidOnBlur());
    instance.on('input', (e: any) => this.$emit('on-search', e.detail.value));
    instance.on('invalid', () => {
      // intentionally empty – Tagify handles invalid styling on the tag itself
    });
  },

  beforeUnmount() {
    this.tagifyInstance?.destroy();
  },

  methods: {
    buildDelimiters(): string | null {
      const chars = [
        this.addTagsOnComma ? ',' : '',
        this.addTagsOnSpace ? ' ' : '',
      ].filter(Boolean);
      return chars.length ? chars.join('|') : null;
    },

    emitInput() {
      const joined = (this.tagifyInstance?.value ?? []).map((t: any) => t.value).join(',');
      // 'add' emits immediately and the debounced 'change' follows with the same value; skip the repeat
      if (joined === this.lastEmittedValue && joined === (this.boundValue || '')) return joined;
      this.lastEmittedValue = joined;
      this.$emit('update:modelValue', joined);
      this.$emit('input', joined);
      return joined;
    },

    onTagAdded(tag: IVoerroTagsInputVal[0]) {
      // tagify's 'change' is debounced (~100ms) but 'add' fires synchronously. Emit now so a
      // tag committed on blur (e.g. by clicking Save) reaches v-model before the click handler runs.
      this.emitInput();
      this.$emit('tag-added', tag);
      return tag;
    },

    onTagRemoved(tag: IVoerroTagsInputVal[0]) {
      this.$emit('tag-removed', tag);
      return tag;
    },

    isValidOnBlur() {
      const leftoverText = this.tagifyInstance?.DOM?.input?.textContent;

      if (!this.validate || !leftoverText) {
        this.$emit('is-valid-input-on-blur', true);
        return true;
      }

      const result = this.validate(leftoverText);
      this.$emit('is-valid-input-on-blur', result);
      return result;
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables.scss';

.rs-form-tags-input-wrapper {
  .tagify {
    --tag-bg: #7d59e1;
    --tag-text-color: #fff;
    --tag-border-radius: 0.4em;
    --tag-pad: 0.35em 0.6em;

    width: 100%;
    border: 1px solid #dbdbdb;
    border-radius: 0.25em;
    padding: 0.5em 0.25em;

    &.tagify--focus {
      border: 1px solid #8bbafe;
      box-shadow: 0 0 0 0.2em rgba(13, 110, 253, 0.25);
      outline: 0 none;
    }

    @include tagify-tag-parity;
  }
}
</style>
